import {
  CloudUploadIcon,
  CogIcon,
  DocumentDownloadIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

const steps = [
  {
    step: '1',
    name: 'Upload Your Audio',
    description:
      'Simply drag & drop your audio or video file, or click to browse. Supports MP3, WAV, MP4, and 10+ formats.',
    details: 'Max file size 5GB • Processing starts instantly',
    icon: CloudUploadIcon,
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  {
    step: '2',
    name: 'AI Transcription Magic',
    description:
      'Our advanced AI analyzes your audio with speaker detection, timestamp precision, and 96%+ accuracy.',
    details: 'Powered by state-of-the-art machine learning models',
    icon: CogIcon,
    color: 'from-purple-500 to-pink-500',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    iconColor: 'text-purple-600 dark:text-purple-400',
  },
  {
    step: '3',
    name: 'Download & Use',
    description:
      'Get your transcription in multiple formats (TXT, SRT, VTT, or DOCX). Ready to use in seconds.',
    details: 'Export options • Share instantly • Edit online',
    icon: DocumentDownloadIcon,
    color: 'from-green-500 to-emerald-500',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    iconColor: 'text-green-600 dark:text-green-400',
  },
];

export default function ThreeStepsProcess() {
  const { t } = useLocale('ai-transcription');

  return (
    <div className='relative isolate overflow-hidden bg-white/50 py-24 dark:bg-slate-900/50 backdrop-blur-sm'>
      {/* Background decoration */}
      <div
        className='absolute left-1/2 top-0 -z-10 -translate-x-1/2 transform-gpu blur-3xl'
        aria-hidden='true'
      >
        <div className='aspect-[1108/632] w-[69.25rem] bg-gradient-to-tr from-primary-200 to-indigo-100 dark:from-primary-900/40 dark:to-indigo-800/20 opacity-30 style={{ clipPath: "polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)" }}' />
      </div>

      <div className='mx-auto max-w-7xl px-6 lg:px-8 relative z-10'>
        <div className='mx-auto max-w-3xl text-center'>
          <div className='mb-6 flex justify-center'>
            <div className='relative inline-flex items-center space-x-2 rounded-full bg-white/60 px-4 py-1.5 text-sm font-bold text-primary-600 ring-1 ring-inset ring-gray-900/10 shadow-sm backdrop-blur-md dark:bg-slate-800/60 dark:text-primary-400 dark:ring-white/10'>
              <span className='mr-2 text-lg leading-none'>⚡</span>
              <span>{t('Simple Process')}</span>
            </div>
          </div>
          <h2 className='text-4xl font-extrabold tracking-tight text-gray-900 dark:text-slate-200 sm:text-6xl text-balance'>
            {t('Transcribe Audio to Text in')}
            <span className='block bg-gradient-to-r from-primary-600 to-pink-600 bg-clip-text text-transparent mt-2'>
              {t('Three Simple Steps')}
            </span>
          </h2>
          <p className='mt-6 text-xl leading-8 text-gray-600 dark:text-slate-400'>
            {t(
              'From upload to download in minutes. No technical skills required.',
            )}
          </p>
        </div>

        <div className='mx-auto mt-20 max-w-7xl'>
          <div className='relative'>
            {/* Connection line for desktop */}
            <div className='absolute top-24 hidden w-full lg:block opacity-50 dark:opacity-30'>
              <div className='flex items-center justify-between px-20'>
                <div className='h-0.5 w-full bg-gradient-to-r from-blue-300 via-purple-300 to-green-300 dark:from-blue-600 dark:via-purple-600 dark:to-green-600 shadow-[0_0_8px_rgba(168,85,247,0.4)]'></div>
              </div>
            </div>

            <div className='grid grid-cols-1 gap-12 lg:grid-cols-3 lg:gap-8'>
              {steps.map((step, stepIdx) => (
                <div key={step.name} className='group relative z-10'>
                  {/* Card (Glassmorphic) */}
                  <div className='relative flex flex-col h-full rounded-3xl bg-white/70 p-8 shadow-xl ring-1 ring-gray-900/5 backdrop-blur-md transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:bg-white dark:bg-slate-800/80 dark:ring-white/10 dark:hover:bg-slate-800 overflow-hidden'>
                    {/* Glowing highlight overlay */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-0 transition-opacity duration-500 group-hover:opacity-[0.03] dark:group-hover:opacity-[0.08]`}
                    ></div>

                    {/* Step number and icon badge */}
                    <div className='relative mb-8 flex items-center justify-between'>
                      <div
                        className={`flex h-16 w-16 items-center justify-center rounded-2xl shadow-sm ring-1 ring-inset ring-black/5 group-hover:scale-110 transition-transform duration-500 ${step.bgColor}`}
                      >
                        <step.icon
                          className={`h-8 w-8 ${step.iconColor}`}
                          aria-hidden='true'
                        />
                      </div>
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-tr ${step.color} text-lg font-black text-white shadow-lg ring-4 ring-white dark:ring-slate-900 group-hover:rotate-12 transition-transform duration-500`}
                      >
                        {step.step}
                      </div>
                    </div>

                    {/* Content */}
                    <div className='relative flex flex-col flex-grow'>
                      <h3 className='text-2xl font-extrabold text-gray-900 dark:text-white mb-4 tracking-tight group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-gray-900 group-hover:to-gray-600 dark:group-hover:from-white dark:group-hover:to-gray-300 transition-all'>
                        {t(step.name)}
                      </h3>
                      <p className='text-base text-gray-600 dark:text-slate-300 mb-6 leading-relaxed flex-grow'>
                        {t(step.description)}
                      </p>

                      {/* Divider */}
                      <div className='h-px w-full bg-gray-100 dark:bg-slate-700/50 mb-4'></div>

                      <div className='flex items-center text-sm font-medium text-gray-500 dark:text-slate-400'>
                        <div
                          className={`mr-3 h-2 w-2 rounded-full bg-gradient-to-r ${step.color} shadow-[0_0_6px_rgba(0,0,0,0.2)]`}
                        ></div>
                        {t(step.details)}
                      </div>
                    </div>

                    {/* Animated arrow for next step */}
                    {stepIdx !== steps.length - 1 && (
                      <div className='absolute z-20 -right-6 top-1/2 hidden -translate-y-1/2 transform lg:block'>
                        <div className='flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg ring-1 ring-gray-900/5 dark:bg-slate-700 dark:ring-white/10 group-hover:ring-primary-500/30 transition-all'>
                          <svg
                            className='h-5 w-5 text-gray-400 group-hover:text-primary-500 transition-transform duration-300 group-hover:translate-x-1 uppercase'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2.5}
                              d='M9 5l7 7-7 7'
                            />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Call to action */}
        <div className='mt-20 text-center relative z-10'>
          <a
            href='/audios'
            className='group relative inline-flex items-center justify-center rounded-full bg-primary-600 px-10 py-5 text-lg font-bold text-white shadow-xl hover:bg-primary-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary-600/30 transition-all duration-300'
          >
            <CloudUploadIcon className='mr-3 h-6 w-6' />
            {t('Start Transcribing Now')}
            <div className='absolute inset-0 rounded-full ring-1 ring-inset ring-white/20'></div>
          </a>
        </div>

        {/* Trust indicators */}
        <div className='mt-14 flex flex-wrap items-center justify-center gap-x-12 gap-y-6 text-sm font-bold text-gray-500 dark:text-slate-400'>
          <div className='flex items-center bg-white/40 dark:bg-slate-800/40 px-4 py-2 rounded-full ring-1 ring-gray-900/5 dark:ring-white/5 backdrop-blur-sm'>
            <div className='mr-3 h-2.5 w-2.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)] animate-pulse'></div>
            {t('876,000+ files processed')}
          </div>
          <div className='flex items-center bg-white/40 dark:bg-slate-800/40 px-4 py-2 rounded-full ring-1 ring-gray-900/5 dark:ring-white/5 backdrop-blur-sm'>
            <div className='mr-3 h-2.5 w-2.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]'></div>
            {t('40+ languages supported')}
          </div>
          <div className='flex items-center bg-white/40 dark:bg-slate-800/40 px-4 py-2 rounded-full ring-1 ring-gray-900/5 dark:ring-white/5 backdrop-blur-sm'>
            <div className='mr-3 h-2.5 w-2.5 rounded-full bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.6)]'></div>
            {t('Enterprise-grade security')}
          </div>
        </div>
      </div>
    </div>
  );
}
